import { composerCertificat } from './modeleCertificat'
import { mesureurCanevas, policePrete } from './mesure'
import { IMAGES_CERTIFICAT } from './CertificatReussite'
import { values as LIBELLES } from '../../../contexts/LanguageContext'

/* ⛔ CERTIFICAT DE REUSSITE, RENDU PDF.

   METHODE : jsPDF, dans le navigateur, en DESSIN VECTORIEL. Les textes sont
   de vrais textes PDF en Questrial (la police de la plateforme, embarquee) :
   nets a tout zoom et a l'impression, selectionnables, lisibles par un
   lecteur d'ecran. Les aplats (fond, badge, coche, coins, filets) sont des
   formes ; seuls le logo et la signature sont des images.
   On n'utilise PAS `react-to-pdf` (deja dans le projet) : il photographie la
   page (html2canvas) et produit une IMAGE dans un PDF, floue au zoom et sans
   texte selectionnable.

   Etape 2 (06/10/2026, decision de Nawal) : « Délivré le » + la date du
   TELECHARGEMENT, comme l'ancien certificat (OLD/src/components/pdf/
   PdfPage.js, `Moment()`). Rien n'est envoye au serveur : tout se fabrique
   dans le navigateur, a partir de ce que l'ecran affiche deja.
   jsPDF est appele par un import dynamique, au clic. ⚠️ Il ne forme PAS un
   morceau separe : `react-to-pdf`, importe par l'espace client
   (AdminDashboard/Progression.js), le met deja dans le paquet principal
   (constate a la construction du 06/10). Le certificat n'ajoute donc pas son
   poids ; l'import dynamique evite surtout de le charger dans les tests qui
   montent le tableau de bord et la fin de module (jsdom n'a pas TextEncoder). */

const URL_POLICE = (process.env.PUBLIC_URL || '') + '/fonts/Questrial-Regular.ttf'
const MM_PAR_POINT = 25.4 / 72

const versBase64 = (octets) => {
  let s = ''
  for (let i = 0; i < octets.length; i += 0x8000) s += String.fromCharCode.apply(null, octets.subarray(i, i + 0x8000))
  return btoa(s)
}
const lireOctets = async (url) => {
  const r = await fetch(url)
  if (!r.ok) throw new Error('certificat : ressource introuvable ' + url)
  return new Uint8Array(await r.arrayBuffer())
}

export const nomDuFichierCertificat = ({ nomModule, langue = 'fr' }) => {
  const base = LIBELLES?.certificatReussite?.nomFichier?.[langue] || 'certificat-de-reussite'
  const module = String(nomModule || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60)
  return base + (module ? '-' + module : '') + '.pdf'
}

/* MIS EN COMMUN avec le bilan soft skills (Bilan/pdfBilan.js, 07/10/2026) :
   les deux documents s'ouvrent et se dessinent de la meme facon. Le
   certificat ne change pas : memes appels, dans le meme ordre.
   `ouvrirDocumentPdf` : une page A4 (paysage ou portrait), Questrial embarquee ;
   `format` : une autre taille en mm, [largeur, hauteur] (image de partage du
   bilan, 07/10/2026).
   `dessinerComposition` : les elements d'une composition (rectangles, traces,
   textes, images), `sources` donnant le fichier de chaque image. Un texte
   peut porter `angle` (degres, sens inverse des aiguilles d'une montre) : le
   texte du tampon du bilan suit son cercle. Sans `angle`, l'appel a jsPDF est
   celui d'avant, au caractere pres. */
export const ouvrirDocumentPdf = async ({ orientation = 'landscape', proprietes, format = 'a4' } = {}) => {
  const { jsPDF } = await import(/* webpackChunkName: "certificat-pdf" */ 'jspdf')
  const doc = new jsPDF({ orientation, unit: 'mm', format, compress: true })
  doc.addFileToVFS('Questrial-Regular.ttf', versBase64(await lireOctets(URL_POLICE)))
  doc.addFont('Questrial-Regular.ttf', 'Questrial', 'normal')
  doc.setFont('Questrial', 'normal')
  if (proprietes) doc.setProperties(proprietes)
  return doc
}

export const dessinerComposition = async (doc, composition, sources) => {
  const images = {}
  for (const el of composition.elements) {
    if (el.type === 'rect') {
      doc.setFillColor(el.couleur)
      doc.rect(el.x, el.y, el.l, el.h, 'F')
    } else if (el.type === 'trace') {
      doc.setFillColor(el.couleur)
      const ops = []
      el.boucles.forEach((b) => {
        b.forEach(([x, y], k) => ops.push({ op: k === 0 ? 'm' : 'l', c: [x, y] }))
        ops.push({ op: 'h', c: [] })
      })
      doc.path(ops).fillEvenOdd()
    } else if (el.type === 'texte') {
      doc.setFontSize(el.taille / MM_PAR_POINT)
      doc.setTextColor(el.couleur)
      const options = { charSpace: el.espacement || 0, baseline: 'alphabetic' }
      if (el.angle) options.angle = el.angle
      doc.text(el.texte, el.x, el.y, options)
    } else if (el.type === 'image') {
      const url = sources?.[el.source]
      if (!url) continue
      if (!images[url]) images[url] = await lireOctets(url)
      doc.addImage(images[url], 'PNG', el.x, el.y, el.l, el.h, el.source, 'SLOW')
    }
  }
  return doc
}

/* Un certificat sans nom de personne ou sans nom de module n'atteste rien :
   on ne le fabrique pas (les boutons ne s'affichent pas non plus). */
export const fabriquerPdfCertificat = async (donnees) => {
  const langue = donnees?.langue || 'fr'
  if (!String(donnees?.prenomNom || '').trim() || !String(donnees?.nomModule || '').trim()) {
    throw new Error('certificat : nom ou module absent, aucun PDF fabriqué')
  }
  await policePrete()
  const composition = composerCertificat({
    ...donnees,
    dateDelivrance: donnees?.dateDelivrance || new Date(),
    mesurer: mesureurCanevas(),
  })

  const titre = LIBELLES?.certificatReussite?.mention?.[langue] || 'Certificat de réussite'
  const doc = await ouvrirDocumentPdf({
    orientation: 'landscape',
    proprietes: {
      title: [titre, donnees?.nomModule, donnees?.prenomNom].filter(Boolean).join(', '),
      author: 'Rising Up',
      creator: 'THE LAB, Rising Up',
    },
  })
  return dessinerComposition(doc, composition, IMAGES_CERTIFICAT)
}

export const telechargerCertificatPdf = async (donnees) => {
  const doc = await fabriquerPdfCertificat(donnees)
  doc.save(nomDuFichierCertificat(donnees))
  return true
}
